// frontend/src/hooks/useAnalytics.js
import { useCallback, useContext, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analytics';

/**
 * Custom hook for analytics tracking with React integration
 */
export const useAnalytics = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // Initialize analytics when user data is available
  useEffect(() => {
    if (user && isAuthenticated) {
      // Set user properties for segmentation
      const userProperties = {
        paymentStatus: user.paymentStatus || 'free',
        companySize: user.companySize || 'unknown',
        useCase: user.useCase || 'unknown',
        authMethod: user.authMethod || 'unknown',
        role: user.role || 'user',
        onboardingCompleted: user.onboardingCompleted || false,
        emailVerified: user.emailVerified || false
      };

      analyticsService.setUserProperties(user.id, userProperties);
    }
  }, [user, isAuthenticated]);

  // Track page views automatically
  useEffect(() => {
    if (analyticsService.isReady()) {
      const pageTitle = document.title;
      const pagePath = location.pathname + location.search;
      
      analyticsService.trackPageView(pagePath, pageTitle, {
        user_authenticated: isAuthenticated,
        user_plan: user?.paymentStatus || 'unknown'
      });
    }
  }, [location, isAuthenticated, user?.paymentStatus]);

  // Memoized tracking functions
  const trackEvent = useCallback((eventName, parameters = {}) => {
    analyticsService.trackEvent(eventName, parameters);
  }, []);

  const trackAuth = useCallback((action, method, success = true, additionalParams = {}) => {
    analyticsService.trackAuth(action, method, success, additionalParams);
  }, []);

  const trackDocument = useCallback((action, documentData = {}) => {
    analyticsService.trackDocument(action, documentData);
  }, []);

  const trackSignature = useCallback((action, signatureData = {}) => {
    analyticsService.trackSignature(action, signatureData);
  }, []);

  const trackPayment = useCallback((action, paymentData = {}) => {
    analyticsService.trackPayment(action, paymentData);
  }, []);

  const trackFeature = useCallback((featureName, action = 'used', additionalParams = {}) => {
    analyticsService.trackFeature(featureName, action, additionalParams);
  }, []);

  const trackOnboarding = useCallback((stepName, stepNumber, totalSteps, completed = false, additionalParams = {}) => {
    analyticsService.trackOnboarding(stepName, stepNumber, totalSteps, completed, additionalParams);
  }, []);

  const trackEngagement = useCallback((action, element, section, additionalParams = {}) => {
    analyticsService.trackEngagement(action, element, section, additionalParams);
  }, []);

  const trackError = useCallback((errorType, errorMessage, location, additionalParams = {}) => {
    analyticsService.trackError(errorType, errorMessage, location, additionalParams);
  }, []);

  const trackPerformance = useCallback((metricName, value, additionalParams = {}) => {
    analyticsService.trackPerformance(metricName, value, additionalParams);
  }, []);

  return {
    // Core tracking functions
    trackEvent,
    trackAuth,
    trackDocument,
    trackSignature,
    trackPayment,
    trackFeature,
    trackOnboarding,
    trackEngagement,
    trackError,
    trackPerformance,
    
    // Utility functions
    isReady: analyticsService.isReady(),
    
    // User context
    currentUser: user,
    isAuthenticated
  };
};

/**
 * Hook for tracking specific component interactions
 */
export const useComponentTracking = (componentName) => {
  const { trackEngagement, trackError } = useAnalytics();

  const trackClick = useCallback((elementName, additionalParams = {}) => {
    trackEngagement('click', elementName, componentName, additionalParams);
  }, [trackEngagement, componentName]);

  const trackView = useCallback((additionalParams = {}) => {
    trackEngagement('view', componentName, 'component', additionalParams);
  }, [trackEngagement, componentName]);

  const trackInteraction = useCallback((interactionType, elementName, additionalParams = {}) => {
    trackEngagement(interactionType, elementName, componentName, additionalParams);
  }, [trackEngagement, componentName]);

  const trackComponentError = useCallback((error, additionalParams = {}) => {
    trackError('component_error', error.message, componentName, {
      component: componentName,
      ...additionalParams
    });
  }, [trackError, componentName]);

  return {
    trackClick,
    trackView,
    trackInteraction,
    trackComponentError
  };
};

/**
 * Hook for form tracking
 */
export const useFormTracking = (formName) => {
  const { trackEvent } = useAnalytics();

  const trackFormStart = useCallback((additionalParams = {}) => {
    trackEvent('form_start', {
      event_category: 'form',
      form_name: formName,
      ...additionalParams
    });
  }, [trackEvent, formName]);

  const trackFormSubmit = useCallback((success = true, additionalParams = {}) => {
    trackEvent('form_submit', {
      event_category: 'form',
      form_name: formName,
      success,
      ...additionalParams
    });
  }, [trackEvent, formName]);

  const trackFieldInteraction = useCallback((fieldName, action, additionalParams = {}) => {
    trackEvent('form_field_interaction', {
      event_category: 'form',
      form_name: formName,
      field_name: fieldName,
      action,
      ...additionalParams
    });
  }, [trackEvent, formName]);

  const trackFormError = useCallback((errorType, fieldName, additionalParams = {}) => {
    trackEvent('form_error', {
      event_category: 'form',
      form_name: formName,
      error_type: errorType,
      field_name: fieldName,
      ...additionalParams
    });
  }, [trackEvent, formName]);

  return {
    trackFormStart,
    trackFormSubmit,
    trackFieldInteraction,
    trackFormError
  };
};

export default useAnalytics;
